import { currency } from 'utils/formats';

export const MENTORSHIP_LEAD_TITLE = 'Mentorship Estimate Enquiry';

export type MentorshipEstimateLine = {
  label: string;
  amount_pence: number;
};

export type MentorshipLeadPreferences = {
  source_page?: string;
  mentorship_format?: string;
  mentorship_format_label?: string;
  mentor_id?: string | null;
  mentor_name?: string | null;
  refresher_days?: number | null;
  estimate_total_pence?: number | null;
  estimate_lines?: MentorshipEstimateLine[];
  mentor_days?: number | null;
  selected_course_ids?: string[];
};

export const isMentorshipLead = (lead?: {
  source?: string;
  metadata?: {
    title?: string;
    type?: string;
    preferences?: MentorshipLeadPreferences;
  };
}) => {
  if (!lead) {
    return false;
  }

  return (
    lead.source === 'mentorship' ||
    lead.metadata?.type === 'mentorship' ||
    lead.metadata?.title === MENTORSHIP_LEAD_TITLE
  );
};

export const getMentorshipPreferencesFromLead = (lead?: {
  metadata?: { preferences?: MentorshipLeadPreferences };
}) => lead?.metadata?.preferences ?? null;

export const formatMentorshipMoney = (pence: number | null | undefined) => {
  if (pence == null) {
    return 'Price on enquiry';
  }

  return currency(pence) ?? 'Price on enquiry';
};

export const buildMentorshipDetailRows = (
  preferences: MentorshipLeadPreferences | null,
) => {
  if (!preferences) {
    return [];
  }

  const rows: Array<{
    label: string;
    value: string;
    multiline?: boolean;
  }> = [];

  if (preferences.mentorship_format_label) {
    rows.push({
      label: 'Format',
      value: preferences.mentorship_format_label,
    });
  }

  if (preferences.mentor_name) {
    rows.push({
      label: 'Preferred mentor',
      value: preferences.mentor_name,
    });
  }

  if (preferences.refresher_days != null && preferences.refresher_days > 0) {
    rows.push({
      label: 'Refresher days',
      value: String(preferences.refresher_days),
    });
  }

  if (preferences.mentor_days != null && preferences.mentor_days > 0) {
    rows.push({
      label: 'Mentor days',
      value: String(preferences.mentor_days),
    });
  }

  if (preferences.estimate_total_pence != null) {
    rows.push({
      label: 'Estimated total',
      value: formatMentorshipMoney(preferences.estimate_total_pence),
    });
  }

  if (preferences.source_page) {
    rows.push({
      label: 'Source page',
      value: preferences.source_page,
    });
  }

  return rows;
};

export const getMentorshipLeadListSummary = (
  preferences: MentorshipLeadPreferences | null,
) => {
  if (!preferences) {
    return 'Mentorship estimate enquiry';
  }

  const parts = [
    preferences.mentorship_format_label,
    preferences.mentor_name,
    preferences.estimate_total_pence != null
      ? formatMentorshipMoney(preferences.estimate_total_pence)
      : null,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(' · ') : 'Mentorship estimate enquiry';
};
